<script>
    import { ValueService } from '../../services/ValueService.js';

    /**
     * @typedef {{value: string, label: string}} LySelectOption
     */

    /**
     * @type {{
     *     name?: string | null,
     *     id?: string | null,
     *     label?: string | null,
     *     options?: LySelectOption[],
     *     autofocus?: boolean,
     *     required?: boolean,
     *     value?: string,
     *     thisRef?: HTMLSelectElement | null,
     *     [key: string]: any,
     * }}
     */
    let {
        name = null,
        id = null,
        label = null,
        options = [],
        autofocus = false,
        required = false,
        value = $bindable(''),
        thisRef = $bindable(null),
        ...restProps
    } = $props();

    const useName = $derived(ValueService.toStringSingleLine(name));
    const useId = $derived(
        ValueService.hasValue(id) ? ValueService.toStringSingleLine(id) : `ly-input-${crypto.randomUUID()}`
    );
    const useOptions = $derived(
        (Array.isArray(options) ? options : []).map((option) => ({
            value: ValueService.toStringSingleLine(option?.value),
            label: ValueService.toStringSingleLine(option?.label),
        }))
    );

    /**
     * Ein <select> zeigt immer einen Eintrag. Passt der Wert zu keiner Option, wird der
     * erste Eintrag dargestellt, damit das Feld nie leer bleibt.
     */
    const useValue = $derived.by(() => {
        const current = ValueService.toStringSingleLine(value);

        return useOptions.some((option) => option.value === current) ? current : (useOptions[0]?.value ?? '');
    });

    /**
     * @type {{
     *     id: string,
     *     name?: string,
     *     autofocus: boolean,
     *     required: boolean,
     * }}
     */
    const selectProps = $derived({
        id: useId,
        ...(useName !== '' ? { name: useName } : {}),
        autofocus: ValueService.toBool(autofocus),
        required: ValueService.toBool(required),
    });

    /**
     * @param {Event & {currentTarget: EventTarget & HTMLSelectElement}} event
     * @returns {void}
     */
    function onSelectChange(event) {
        value = event.currentTarget.value;
    }
</script>

<div class="lyInput lyInputSelect relative w-full">
    <select
        {...restProps}
        {...selectProps}
        value={useValue}
        onchange={onSelectChange}
        bind:this={thisRef}
        class="focus:bg-secondary-100 required:invalid:bg-warning-150 disabled:bg-black-50
            border-black-300 required:invalid:border-warning-500 disabled:border-black-200
            disabled:text-black-300 relative z-1 m-0
            block w-full max-w-md
            cursor-pointer rounded-sm
            border-1
            bg-(--dwc-input-bg-color) px-2
            py-1 text-(length:--dwc-input-font-size)
            outline-0
            focus:ring
            disabled:pointer-events-none">
        {#each useOptions as option}
            <option value={option.value}>{option.label}</option>
        {/each}
    </select>
    {#if ValueService.isDefined(label)}
        <label
            for={useId}
            class="pointer-events-none absolute z-10 m-0 block
                   rounded-sm border border-(--dwc-border-color) bg-(--dwc-label-bg-color)
                   leading-none
                   transition-all
                   duration-150 ease-in-out">
            {ValueService.toStringSingleLine(label)}
        </label>
    {/if}
</div>

<style>
    .lyInputSelect {
        --dwc-input-bg-color: #fff;
        --dwc-input-font-size: 1rem;

        /* Ein <select> ist nie "leer" – das Label liegt deshalb dauerhaft oben,
           statt wie bei LyInputText erst bei Fokus oder Inhalt hochzuwandern. */
        label {
            --dwc-input-font-size: 0.65rem;
            --dwc-label-bg-color: var(--dwc-input-bg-color);
            --dwc-border-color: #666;
            color: #666;
            opacity: 1;
            line-height: 1;
            font-weight: normal;
            font-style: normal;
            font-size: var(--dwc-input-font-size);
            left: 0.3rem;
            top: -0.65rem;
            padding: 0.125rem 0.25rem;
        }
    }
</style>
